import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { EventContent, LiveInfo } from "@tih/shared";
import { RsvpModal } from "../RsvpModal";
import { useEventContent } from "../../context/EventContentContext";
import { useCountdown } from "../../hooks/useCountdown";
import { useRsvpReservation } from "../../hooks/useRsvpReservation";
import { formatRsvpThanksLine } from "../../lib/eventDisplay";
import { shouldOfferPublicCheckIn } from "../../lib/public-check-in";
import { COMING_SOON_VENUE } from "../../lib/coming-soon-venue";
import { isBroadcastLive } from "../../lib/live-display";
import { LiveLandingPage } from "../../pages/LiveLandingPage";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "Africa/Kampala",
});

type Props = {
  liveInfo: LiveInfo | null;
  eventId?: number | null;
  eventPhase?: EventContent["eventPhase"];
  dateIso: string;
  posterUrl?: string | null;
  posterCaption?: string | null;
  showLiveVideo?: boolean;
  showCountdown?: boolean;
  showWhen?: boolean;
  /** Override the primary “When” line (e.g. when no upcoming date is scheduled). */
  whenLabel?: string;
  /** Override the secondary “When” line. */
  whenSub?: string;
  showRsvp?: boolean;
  showHeroLogo?: boolean;
  thanksIso?: string;
  /** Override RSVP success line when no scheduled event date exists. */
  thanksLine?: string;
  /** Compact “How to participate” under the RSVP card (coming-soon landing). */
  showParticipate?: boolean;
};

export function ComingSoonEventGrid({
  liveInfo,
  eventId,
  eventPhase,
  dateIso,
  posterUrl,
  posterCaption,
  showLiveVideo = false,
  showCountdown = false,
  showWhen = true,
  whenLabel,
  whenSub,
  showRsvp = true,
  showHeroLogo = true,
  thanksIso,
  thanksLine,
  showParticipate = false,
}: Props) {
  const content = useEventContent();
  const resolvedEventId = eventId ?? content.eventId ?? null;
  const reservation = useRsvpReservation(resolvedEventId);
  const checkInOpen = shouldOfferPublicCheckIn(liveInfo, eventPhase ?? content.eventPhase);
  const checkedIn = Boolean(reservation?.checkedInAt);
  const target = useMemo(() => Date.parse(dateIso), [dateIso]);
  const { d, h, m, s } = useCountdown(target);
  const [rsvpOpen, setRsvpOpen] = useState(false);

  const eventDate = new Date(target);
  const dateLabel = whenLabel ?? DATE_FMT.format(eventDate);
  const timeLabel = whenSub ?? `${TIME_FMT.format(eventDate)} EAT · Kampala`;
  const caption = posterCaption ?? "Together In Heaven · Worship Overnight";
  const rsvpThanks =
    thanksLine ??
    ((thanksIso ?? dateIso)
      ? formatRsvpThanksLine(thanksIso ?? dateIso)
      : undefined);

  void showHeroLogo;
  const showPoster = Boolean(posterUrl);
  const live = isBroadcastLive(liveInfo);
  /** Live broadcast still leads in the hero. Replay is deferred to the page (after directions). */
  const videoFocus = showLiveVideo && (!showPoster || live);
  const deferReplay = showLiveVideo && showPoster && !live;
  /** Only mount the player in the hero when live (or no poster). Replay mounts elsewhere. */
  const showHeroVideo = showLiveVideo && !deferReplay;

  const gridClass = [
    "coming-soon-grid",
    "coming-soon-grid--poster-left",
    videoFocus ? "coming-soon-grid--video-focus" : "",
    deferReplay || showPoster ? "coming-soon-grid--poster-lead" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const eventBar = (
    <div className="coming-soon-event-bar" aria-label="Event details">
      {showWhen && (
        <div className="cs-bar-col">
          <div className="cs-bar-k">When</div>
          <div className="cs-bar-v">{dateLabel}</div>
          <div className="cs-bar-sub">{timeLabel}</div>
        </div>
      )}

      <div className="cs-bar-col">
        <div className="cs-bar-k">Where</div>
        <div className="cs-bar-v">{COMING_SOON_VENUE.building}</div>
        <div className="cs-bar-sub">{COMING_SOON_VENUE.area}</div>
      </div>

      {showCountdown && (
        <div className="cs-bar-col">
          <div className="cs-bar-k cs-bar-k--muted">Begins in</div>
          <div className="cs-bar-countdown-nums">
            <span><b>{d}</b></span>
            <span><b>{h}</b></span>
            <span><b>{m}</b></span>
            <span><b>{s}</b></span>
          </div>
          <div className="cs-bar-countdown-units">
            <span>days</span>
            <span>hrs</span>
            <span>min</span>
            <span>sec</span>
          </div>
        </div>
      )}

      {showRsvp && (
        <div className="cs-bar-col">
          <div className="cs-bar-k">
            {checkInOpen ? "Check in" : reservation ? "Your seat" : "RSVP"}
          </div>
          <div className="cs-bar-rsvp">
            <button
              type="button"
              className="btn btn-primary cs-bar-rsvp-btn"
              onClick={() => setRsvpOpen(true)}
            >
              {checkInOpen
                ? checkedIn
                  ? "You're checked in"
                  : "Check in"
                : reservation
                  ? "Change reservation"
                  : "I plan to attend"}
            </button>
          </div>
          <div className={reservation || checkedIn ? "cs-bar-sub" : "cs-bar-sub cs-bar-sub--empty"}>
            {checkInOpen && checkedIn
              ? reservation?.name.trim()
                ? `${reservation.name.trim()} · online`
                : "Watching online"
              : reservation
                ? reservation.name.trim()
                  ? `${reservation.name.trim()} · ${reservation.seats} seat${reservation.seats === 1 ? "" : "s"}`
                  : `You're coming · ${reservation.seats} seat${reservation.seats === 1 ? "" : "s"}`
                : null}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={gridClass}>
      <div className="coming-soon-copy">
        {(showHeroVideo || showCountdown || showRsvp || showWhen) && (
          <div className="coming-soon-stream-stack">
            {showHeroVideo && (
              <div className="coming-soon-live-video">
                <LiveLandingPage initialInfo={liveInfo} />
              </div>
            )}
            {eventBar}
            {showParticipate && (
              <div className="coming-soon-participate" aria-label="How to participate">
                <div className="coming-soon-participate-k">How to participate</div>
                <p className="coming-soon-participate-body">
                  Reserve a seat, come to {COMING_SOON_VENUE.mapTitle} on the night,
                  or watch with us online. Share your story, prepare for the night,
                  or walk with us through prayer — your voice belongs here.
                </p>
                <Link to="/participate" className="coming-soon-participate-link">
                  Explore all ways to participate
                  <span className="arr" aria-hidden="true">→</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {showRsvp && (
          <RsvpModal
            open={rsvpOpen}
            onClose={() => setRsvpOpen(false)}
            eventId={resolvedEventId}
            thanksLine={rsvpThanks}
            title={
              checkInOpen
                ? checkedIn
                  ? "You're checked in"
                  : "Check in"
                : reservation
                  ? "Your reservation"
                  : "Plan to attend"
            }
            mode={checkInOpen ? "check-in" : "rsvp"}
            shareDateIso={thanksIso ?? dateIso}
            shareEventTitle={caption}
          />
        )}
      </div>

      {showPoster && (
        <div className="coming-soon-poster" id="poster">
          <div className="coming-soon-poster-frame">
            <img src={posterUrl!} alt={`${caption} poster`} />
          </div>
          <div className="coming-soon-poster-caption">{caption}</div>
        </div>
      )}
    </div>
  );
}
